import { Drawer, Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { TagsOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../../../store/AuthContext/AuthContext';
import { authRequest } from '../../../../../../common/request/authRequest';
import moment from 'moment';
import { Column } from '@ant-design/charts';
import { DrawerPanelTitle, drawerPanelStyles } from '../../../../common/Drawers/DrawerPanelTitle';
import { EmptyStateBox } from '../../../../../components/design/EmptyStateBox/EmptyStateBox';

const CATEGORY_META = [
	{ key: 'Sin texto', color: '#c5cdd6', description: 'Reseñas sin comentario' },
	{ key: 'Con texto - Sin etiquetar', color: '#FFC64E', description: 'Con texto, pendientes de etiquetar' },
	{ key: 'Con texto - Etiquetadas', color: 'MediumSeaGreen', description: 'Con texto y ya etiquetadas' },
] as const;

export const LabeledDrawer = (props: { onFinish: any }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [history, setHistory] = useState<any[] | null>(null);
	const { projectGroup } = useAuth();

	useEffect(() => {
		setIsLoading(true);
		authRequest(
			`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/comments/historic`,
			{ method: 'GET' }
		)
			.then((data) => {
				setHistory(Array.isArray(data) ? data : []);
			})
			.finally(() => setIsLoading(false));
	}, [projectGroup.project, projectGroup.group]);

	const totals = useMemo(() => {
		const result: Record<string, number> = {
			'Sin texto': 0,
			'Con texto - Sin etiquetar': 0,
			'Con texto - Etiquetadas': 0,
		};
		(history ?? []).forEach((point: any) => {
			if (point?.category in result) {
				result[point.category] += Number(point.value) || 0;
			}
		});
		return result;
	}, [history]);

	const config: any = {
		data: history ?? [],
		autoFit: true,
		isStack: true,
		xField: 'x',
		yField: 'value',
		seriesField: 'category',
		meta: {
			category: {
				values: CATEGORY_META.map((item) => item.key),
			},
		},
		maxColumnWidth: 48,
		columnWidthRatio: 0.55,
		xAxis: {
			label: {
				formatter: (text: string) => moment(text, 'YYYY-MM').format('MMM YYYY'),
				style: { fill: '#64748b', fontSize: 12 },
			},
			line: { style: { stroke: '#e2e8f0' } },
			tickLine: null,
		},
		yAxis: {
			label: {
				style: { fill: '#94a3b8', fontSize: 11 },
			},
			grid: {
				line: { style: { stroke: '#eef2f7', lineDash: [4, 4] } },
			},
		},
		legend: false,
		// Sin números encima de cada segmento: el detalle va al tooltip
		label: false,
		columnStyle: {
			radius: [4, 4, 0, 0],
		},
		color: ({ category }: any) => {
			const found = CATEGORY_META.find((item) => item.key === category);
			return found?.color ?? '#1890ff';
		},
		tooltip: {
			shared: true,
			showMarkers: false,
			domStyles: {
				'g2-tooltip': {
					background: '#fff',
					border: '1px solid #dbe3ec',
					borderRadius: '10px',
					boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
					padding: '10px 12px',
					opacity: 1,
				},
				'g2-tooltip-title': {
					color: '#1a202c',
					fontWeight: 700,
					fontSize: '13px',
					marginBottom: '8px',
				},
				'g2-tooltip-list-item': {
					color: '#475569',
					fontSize: '12px',
					marginBottom: '4px',
				},
				'g2-tooltip-value': {
					color: '#1a202c',
					fontWeight: 700,
				},
			},
			title: (title: string) => moment(title, 'YYYY-MM').format('MMMM YYYY'),
			formatter: (datum: any) => ({
				name: datum.category,
				value: Number(datum.value || 0).toLocaleString(),
			}),
		},
		interactions: [{ type: 'active-region' }],
		state: {
			active: {
				style: {
					fillOpacity: 0.95,
					stroke: '#fff',
					lineWidth: 1,
				},
			},
		},
	};

	return (
		<Drawer
			placement="right"
			width="60%"
			title={
				<DrawerPanelTitle
					title="Histórico de etiquetado"
					//subtitle="Evolución de los últimos 6 meses: reseñas etiquetadas frente a pendientes"
					isProject={false}
				/>
			}
			onClose={() => props.onFinish()}
			open={true}
			{...drawerPanelStyles}
		>
			{isLoading ? (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 420 }}>
					<Spin />
				</div>
			) : !history?.length ? (
				<div style={{ padding: 24 }}>
					<EmptyStateBox
						icon={<TagsOutlined />}
						title="Sin histórico de etiquetado"
						description="Cuando haya reseñas etiquetadas verás aquí su evolución mensual."
					/>
				</div>
			) : (
				<div style={{ padding: '20px 24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
							gap: 12,
						}}
					>
						{CATEGORY_META.map((item) => (
							<div
								key={item.key}
								style={{
									background: '#fff',
									border: '1px solid #dbe3ec',
									borderLeft: `3px solid ${item.color}`,
									borderRadius: '0 10px 10px 0',
									padding: '12px 14px',
								}}
							>
								<div style={{ fontSize: 12, color: 'LightSlateGray', marginBottom: 4 }}>
									{item.description}
								</div>
								<div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
									<div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>
										{item.key}
									</div>
									<div style={{ fontSize: 20, fontWeight: 700, color: '#1a202c', lineHeight: 1 }}>
										{totals[item.key].toLocaleString()}
									</div>
								</div>
							</div>
						))}
					</div>

					<div
						style={{
							background: '#fff',
							border: '1px solid #dbe3ec',
							borderRadius: 12,
							padding: '16px 18px 12px',
						}}
					>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								marginBottom: 12,
								gap: 12,
								flexWrap: 'wrap',
							}}
						>
							<div style={{ fontSize: 13, fontWeight: 700, color: 'LightSlateGray' }}>
								Distribución de etiquetado mes a mes
							</div>
							<div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
								{CATEGORY_META.map((item) => (
									<div
										key={item.key}
										style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}
									>
										<span
											style={{
												width: 10,
												height: 10,
												borderRadius: 2,
												background: item.color,
												flexShrink: 0,
											}}
										/>
										{item.key}
									</div>
								))}
							</div>
						</div>

						<div style={{ minHeight: 360, width: '100%' }}>
							<Column {...config} />
						</div>
					</div>
				</div>
			)}
		</Drawer>
	);
};
